import { PocRadio } from 'commons/components/components-wrapper/poc-radio.component';
import { PocRadioGroup } from 'commons/components/components-wrapper/poc-radio-group.component';
import { Input, InputFileUploader, Select } from 'commons/components';
import { OnFileChangeType } from 'commons/components/input-file-uploader/input-file-uploader.component';
import { fileTypeAcceptedForCertificates } from 'modules/settings/pods/settings-devices/settings-devices-eap-settings/settings-devices-eap-settings-form/settings-devices-eap-settings-form.constants';
import React from 'react';
import { getSingleSelectValue } from '../settings-devices-eap-settings.utils';
import { isValidCertificateFile } from './settings-devices-eap-settings-importing.business';
import {
    EapImportFieldDefinition,
    EapImportFieldKey,
    EapImportFieldValue,
    EapImportMappings,
    ImportSourceType,
} from './settings-devices-eap-settings-importing.model';
import {
    EapSettingsImportingControlsWrapper,
    EapSettingsImportingFieldControl,
    EapSettingsImportingFieldSection,
    EapSettingsImportingMappingRow,
    EapSettingsImportingRadioWrapper,
    EapSettingsImportingTitle,
} from './settings-devices-eap-settings-importing.style';

type TranslationFunction = (key: string, options?: Record<string, unknown>) => string;

type SelectOption = {
    value: string;
    text: string;
};

type Props = {
    field: EapImportFieldDefinition;
    fieldValue: EapImportFieldValue;
    fileColumnOptions: SelectOption[];
    t: TranslationFunction;
    updateMapping: (field: EapImportFieldKey, data: Partial<EapImportMappings[EapImportFieldKey]>) => void;
};

export const SettingsDevicesEapSettingsImportingFieldComponent: React.FC<Props> = ({
    field,
    fieldValue,
    fileColumnOptions,
    t,
    updateMapping,
}) => {
    const renderFieldFileControl = () => (
        <Select
            name={`${field.key}-source-file`}
            value={fieldValue.fileColumn}
            placeholder={t(
                'settings.devices.eap-settings.eap-config-list.action.import.importing.field.file.placeholder'
            )}
            options={fileColumnOptions}
            onChange={(_name, value) => {
                const fileColumn = (getSingleSelectValue(value) as string) || '';

                updateMapping(field.key, {
                    fileColumn,
                    ...(fileColumn ? { source: 'FILE' as ImportSourceType } : {}),
                });
            }}
            clearButton
        />
    );

    const renderFieldFixedControl = () => {
        if (field.controlType === 'select') {
            return (
                <Select
                    name={`${field.key}-source-fixed-select`}
                    value={fieldValue.fixedValue}
                    placeholder={t(
                        field.fixedPlaceholderKey ??
                            'settings.devices.eap-settings.eap-config-list.action.import.importing.field.fixed.select.placeholder'
                    )}
                    options={field.fixedOptions ?? []}
                    onChange={(_name, value) => {
                        const fixedValue = (getSingleSelectValue(value) as string) || '';

                        updateMapping(field.key, {
                            fixedValue,
                            ...(fixedValue ? { source: 'FIXED' as ImportSourceType } : {}),
                        });
                    }}
                />
            );
        }

        if (field.controlType === 'file') {
            const isInvalid =
                fieldValue.source === 'FIXED' &&
                fieldValue.fixedFileName &&
                !isValidCertificateFile(fieldValue.fixedFileName);

            return (
                <InputFileUploader
                    textButton={t('settings.devices.eap-settings.eap-config-create-form.button.browse')}
                    placeholder={t(
                        field.fixedPlaceholderKey ??
                            'settings.devices.eap-settings.eap-config-list.action.import.importing.field.fixed-file.placeholder'
                    )}
                    accept={fileTypeAcceptedForCertificates.join(',')}
                    fileName={fieldValue.fixedFileName}
                    onFileChange={(value: OnFileChangeType) =>
                        updateMapping(field.key, {
                            fixedFile: value.file,
                            fixedFileName: value.fileName,
                            ...(value.file ? { source: 'FIXED' as ImportSourceType } : {}),
                        })
                    }
                    dataTestId={`import-${field.key}`}
                    validity={{ state: isInvalid ? 'error' : 'valid', type: ['custom'] }}
                    validationMessages={{
                        error: {
                            custom: t(
                                'settings.devices.eap-settings.eap-config-list.action.import.modal.upload.status.invalid'
                            ),
                        },
                    }}
                />
            );
        }

        return (
            <Input
                name={`${field.key}-source-fixed-input`}
                placeholder={field.fixedPlaceholderKey ?? ''}
                value={fieldValue.fixedValue}
                onChange={(_field, value) =>
                    updateMapping(field.key, {
                        fixedValue: value ?? '',
                    })
                }
                type={field.fixedInputType ?? (field.key === 'password' ? 'password' : 'text')}
                maxlength={field.fixedMaxLength}
                maxValue={field.fixedMaxLength}
                showCharCount={field.fixedShowCharCount}
            />
        );
    };

    return (
        <EapSettingsImportingFieldSection>
            <EapSettingsImportingTitle variant="body1">{t(field.labelKey)}</EapSettingsImportingTitle>
            <EapSettingsImportingMappingRow>
                <EapSettingsImportingRadioWrapper>
                    <PocRadioGroup
                        name={`${field.key}-source-type`}
                        direction="vertical"
                        value={fieldValue.source}
                        onValueChange={(event) =>
                            updateMapping(field.key, {
                                source: event.detail as ImportSourceType,
                            })
                        }
                    >
                        <PocRadio value="FILE">
                            {t('settings.devices.eap-settings.eap-config-list.action.import.importing.source.file')}
                        </PocRadio>
                        <PocRadio value="FIXED">
                            {t(
                                field.fixedSourceLabelKey ??
                                    'settings.devices.eap-settings.eap-config-list.action.import.importing.source.fixed'
                            )}
                        </PocRadio>
                    </PocRadioGroup>
                </EapSettingsImportingRadioWrapper>
                <EapSettingsImportingControlsWrapper>
                    <EapSettingsImportingFieldControl>{renderFieldFileControl()}</EapSettingsImportingFieldControl>
                    <EapSettingsImportingFieldControl>{renderFieldFixedControl()}</EapSettingsImportingFieldControl>
                </EapSettingsImportingControlsWrapper>
            </EapSettingsImportingMappingRow>
        </EapSettingsImportingFieldSection>
    );
};
